import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, Plus, X, Sparkles, Cloud } from "lucide-react";
import { useAvailableModels, groupModelsByTier } from "@/hooks/useAvailableModels";
import type { ModelMeta } from "@/lib/aiModels";

const TASK_CLASSES = ["fast", "balanced", "reasoning", "verifier", "multimodal", "retrieval"];

export interface PolicyFormState {
  task_class: string;
  preferred_model: string; // full id like "google/gemini-3-flash-preview"
  fallback_chain: string[]; // ordered list of model ids
  temperature_default: string;
  max_tokens: string;
  sla_max_latency_ms: string;
}

interface Props {
  value: PolicyFormState;
  onChange: (v: PolicyFormState) => void;
  onSubmit: () => void;
  isSaving: boolean;
  isEditing: boolean;
}

function ModelOption({ m }: { m: ModelMeta }) {
  return (
    <div className="flex items-center gap-2 w-full">
      {m.source === "lovable" ? (
        <Cloud className="w-3 h-3 text-primary shrink-0" />
      ) : (
        <Sparkles className="w-3 h-3 text-purple-500 shrink-0" />
      )}
      <span className="flex-1 truncate">{m.label}</span>
      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{m.tier}</Badge>
    </div>
  );
}

function ModelSelect({
  value,
  onChange,
  models,
  placeholder = "Selecione um modelo",
  exclude = [],
}: {
  value: string;
  onChange: (v: string) => void;
  models: ModelMeta[];
  placeholder?: string;
  exclude?: string[];
}) {
  const filtered = models.filter((m) => !exclude.includes(m.id));
  const grouped = useMemo(() => groupModelsByTier(filtered), [filtered]);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {(["reasoning", "balanced", "fast"] as const).map((tier) =>
          grouped[tier].length ? (
            <div key={tier}>
              <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase">{tier}</div>
              {grouped[tier].map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <ModelOption m={m} />
                </SelectItem>
              ))}
            </div>
          ) : null,
        )}
      </SelectContent>
    </Select>
  );
}

export function PolicyForm({ value, onChange, onSubmit, isSaving, isEditing }: Props) {
  const { data: models = [], isLoading } = useAvailableModels();

  const update = <K extends keyof PolicyFormState>(k: K, v: PolicyFormState[K]) =>
    onChange({ ...value, [k]: v });

  const addFallback = (id: string) => {
    if (!id || value.fallback_chain.includes(id) || id === value.preferred_model) return;
    update("fallback_chain", [...value.fallback_chain, id]);
  };

  const removeFallback = (idx: number) => {
    const next = [...value.fallback_chain];
    next.splice(idx, 1);
    update("fallback_chain", next);
  };

  const moveFallback = (idx: number, dir: -1 | 1) => {
    const next = [...value.fallback_chain];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update("fallback_chain", next);
  };

  const exclude = [value.preferred_model, ...value.fallback_chain].filter(Boolean);
  const labelFor = (id: string) => models.find((m) => m.id === id)?.label ?? id;
  const tierFor = (id: string) => models.find((m) => m.id === id)?.tier;

  const canSubmit = value.task_class && value.preferred_model && !isSaving;

  return (
    <div className="space-y-4">
      <div>
        <Label>Task Class</Label>
        <Select value={value.task_class} onValueChange={(v) => update("task_class", v)}>
          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            {TASK_CLASSES.map((tc) => <SelectItem key={tc} value={tc}>{tc}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Modelo Preferido</Label>
        <ModelSelect
          value={value.preferred_model}
          onChange={(v) => update("preferred_model", v)}
          models={models}
          placeholder={isLoading ? "Carregando..." : "Escolha o modelo principal"}
        />
        <p className="text-xs text-muted-foreground mt-1">
          <Cloud className="w-3 h-3 inline mr-1 text-primary" /> Lovable ·
          <Sparkles className="w-3 h-3 inline mx-1 text-purple-500" /> BYOK (provedores próprios)
        </p>
      </div>

      <div>
        <Label>Cadeia de Fallback</Label>
        <p className="text-xs text-muted-foreground mb-2">
          Em caso de erro 402/429 ou latência acima do SLA, o sistema tenta os modelos abaixo na ordem.
        </p>

        {value.fallback_chain.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {value.fallback_chain.map((id, i) => (
              <div key={`${id}-${i}`} className="flex items-center gap-2 p-2 rounded-md bg-muted/40 border">
                <Badge variant="secondary" className="text-xs">#{i + 1}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{labelFor(id)}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate">{id}</p>
                </div>
                {tierFor(id) && <Badge variant="outline" className="text-[10px]">{tierFor(id)}</Badge>}
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => moveFallback(i, -1)}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === value.fallback_chain.length - 1} onClick={() => moveFallback(i, 1)}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeFallback(i)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex-1">
            <ModelSelect
              value=""
              onChange={addFallback}
              models={models}
              placeholder="+ Adicionar modelo de fallback"
              exclude={exclude}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Temperatura</Label>
          <Input type="number" step="0.1" min="0" max="2" value={value.temperature_default} onChange={(e) => update("temperature_default", e.target.value)} />
        </div>
        <div>
          <Label>Max Tokens</Label>
          <Input type="number" value={value.max_tokens} onChange={(e) => update("max_tokens", e.target.value)} />
        </div>
      </div>

      <div>
        <Label>SLA latência máx. (ms) — opcional</Label>
        <Input type="number" placeholder="ex: 8000" value={value.sla_max_latency_ms} onChange={(e) => update("sla_max_latency_ms", e.target.value)} />
        <p className="text-xs text-muted-foreground mt-1">
          Acima desse limite, o provedor é marcado como lento por 2min e o próximo da cadeia é usado.
        </p>
      </div>

      <Button className="w-full" disabled={!canSubmit} onClick={onSubmit}>
        {isSaving ? "Salvando..." : isEditing ? "Atualizar Política" : "Criar Política"}
      </Button>
    </div>
  );
}
